/**
 * SEO landing page content for all 25 services.
 * Used by ServiceLandingPage.js. Slug must match route /service/:serviceSlug.
 */
import {
  SITE_LOGO_URL,
  OG_SHARE_IMAGE_URL,
  SITE_BRAND_NAME,
  SITE_BRAND_LEGACY,
  SITE_BRAND_LEGACY_UPPER
} from '../constants/siteBranding';
import { PILLAR_EXTENDED_CONTENT } from './seoPillarContentData';
import { buildExpandedValetParking } from './landingSeoExpandedOfferings';

const SITE_NAME = SITE_BRAND_NAME;

/** Appended to catalogue service meta descriptions (local corridors + rebrand continuity). */
const SEO_SVC_DESC_TAIL =
  ' Coverage: SG Highway, Satellite, Bodakdev, Thaltej, Bopal, Vastrapur, Prahlad Nagar, Gota, Naroda, Chandkheda, Sanand, Changodar. ' +
  SITE_NAME +
  ' (formerly ' +
  SITE_BRAND_LEGACY +
  ').';

/** Appended to catalogue service meta keywords. */
const SEO_SVC_KW_TAIL =
  ', khodal housekeeping agency ahmedabad, contract facility staff ahmedabad, janitorial contractor gujarat, soft services ahmedabad, ' +
  SITE_BRAND_LEGACY.toLowerCase() +
  ' ahmedabad';

const buildSection = (id, heading, paragraphs = [], listItems = []) => ({ id, heading, paragraphs, listItems });
const buildFaq = (question, answer) => ({ question, answer });

export const SERVICE_SLUGS = [
  'house-keeping-sweeper-ahmedabad',
  'office-boy-peon-ahmedabad',
  'receptionist-ahmedabad',
  'helper-ahmedabad',
  'labour-ahmedabad',
  'manpower-supply-ahmedabad',
  'commercial-building-cleaning-staff-ahmedabad',
  'recency-cleaning-staff-ahmedabad',
  'corporate-cleaning-staff-ahmedabad',
  'hotel-cleaning-staff-ahmedabad',
  'hospital-cleaning-staff-ahmedabad',
  'restaurant-cleaning-staff-ahmedabad',
  'cafe-cleaning-staff-ahmedabad',
  'malls-cleaning-staff-ahmedabad',
  'showroom-cleaning-staff-ahmedabad',
  'shop-cleaning-staff-ahmedabad',
  'office-cleaning-staff-ahmedabad',
  'pest-control-services-ahmedabad',
  'society-cleaning-staff-ahmedabad',
  'valet-parking-ahmedabad',
  'driver-service-agent-ahmedabad',
  'deep-cleaning-service-ahmedabad',
  'special-event-cleaning-works-ahmedabad',
  'carpet-cleaning-service-ahmedabad',
  'house-cleaning-ahmedabad'
];

/** Map from list label (as shown on Services page) to slug for linking */
export const SERVICE_LABEL_TO_SLUG = {
  'House Keeping Sweeper': 'house-keeping-sweeper-ahmedabad',
  'Office Boy, Peon': 'office-boy-peon-ahmedabad',
  'Receptionist': 'receptionist-ahmedabad',
  'Helper': 'helper-ahmedabad',
  'Labour': 'labour-ahmedabad',
  'Manpower Supply': 'manpower-supply-ahmedabad',
  'Commercial Building Cleaning Staff': 'commercial-building-cleaning-staff-ahmedabad',
  'Recency Cleaning Staff': 'recency-cleaning-staff-ahmedabad',
  'Corporate Cleaning Staff': 'corporate-cleaning-staff-ahmedabad',
  'Hotel Cleaning Staff': 'hotel-cleaning-staff-ahmedabad',
  'Hospital Cleaning Staff': 'hospital-cleaning-staff-ahmedabad',
  'Restaurant Cleaning Staff': 'restaurant-cleaning-staff-ahmedabad',
  'Cafe Cleaning Staff': 'cafe-cleaning-staff-ahmedabad',
  'Malls Cleaning Staff': 'malls-cleaning-staff-ahmedabad',
  'Showroom Cleaning Staff': 'showroom-cleaning-staff-ahmedabad',
  'Shop Cleaning Staff': 'shop-cleaning-staff-ahmedabad',
  'Office Cleaning Staff': 'office-cleaning-staff-ahmedabad',
  'Pest Control Services': 'pest-control-services-ahmedabad',
  'Society Cleaning Staff': 'society-cleaning-staff-ahmedabad',
  'Valet Parking Service': 'valet-parking-ahmedabad',
  'Driver Service Agent': 'driver-service-agent-ahmedabad',
  'Deep Cleaning Service': 'deep-cleaning-service-ahmedabad',
  'Special Event Cleaning Works': 'special-event-cleaning-works-ahmedabad',
  'Carpet Cleaning Service': 'carpet-cleaning-service-ahmedabad',
  'House Cleaning': 'house-cleaning-ahmedabad'
};

/** Raster first (OG art) so search/AI can pick a JPEG thumbnail; SVG logo second. */
const SCHEMA_BUSINESS_IMAGES = [OG_SHARE_IMAGE_URL, SITE_LOGO_URL];

const providerSchema = {
  '@type': 'LocalBusiness',
  name: SITE_NAME,
  alternateName: [SITE_BRAND_LEGACY, SITE_BRAND_LEGACY_UPPER],
  image: SCHEMA_BUSINESS_IMAGES,
  logo: SITE_LOGO_URL,
  telephone: '+919265472938',
  address: { '@type': 'PostalAddress', streetAddress: 'Office No. 210, Ravija Plaza, Thaltej Shilaj Road', addressLocality: 'Ahmedabad', addressRegion: 'Gujarat', postalCode: '380059', addressCountry: 'IN' },
  areaServed: { '@type': 'City', name: 'Ahmedabad', containedInPlace: { '@type': 'State', name: 'Gujarat' } },
  hasMap: 'https://maps.app.goo.gl/UtJBLHx2XrZozVJNA'
};

export function buildServiceSchema(name, description) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    image: OG_SHARE_IMAGE_URL,
    provider: providerSchema,
    areaServed: { '@type': 'City', name: 'Ahmedabad', containedInPlace: { '@type': 'State', name: 'Gujarat' } }
  };
}

export function buildFaqSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer }
    }))
  };
}

const SITE_ORIGIN = 'https://www.khodalfacilitymanagementservice.com';
export function buildBreadcrumbSchema(breadcrumbName, canonical) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
      { '@type': 'ListItem', position: 2, name: 'Services', item: SITE_ORIGIN + '/services' },
      { '@type': 'ListItem', position: 3, name: breadcrumbName, item: canonical }
    ]
  };
}

const SERVICE_LANDING_CONTENT_BASE = {
  'house-keeping-sweeper-ahmedabad': {
    slug: 'house-keeping-sweeper-ahmedabad',
    name: 'House Keeping Sweeper',
    metaTitle: 'House Keeping Sweeper in Ahmedabad | Cleaning Staff | ' + SITE_NAME,
    metaDescription: 'House keeping sweeper service in Ahmedabad. Trained sweepers for offices, societies, commercial buildings. Khodal Housekeeping Agency provides reliable house keeping staff across Ahmedabad & Gujarat. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'house keeping sweeper ahmedabad, sweeper service ahmedabad, house keeping staff ahmedabad, office sweeper, cleaning staff ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'House Keeping Sweeper Service in Ahmedabad',
    intro: `${SITE_NAME} provides trained house keeping sweepers in Ahmedabad for offices, societies, commercial buildings, and institutions. Reliable, verified staff for daily sweeping and basic cleaning across Ahmedabad and Gujarat.`,
    breadcrumbName: 'House Keeping Sweeper Ahmedabad',
    sections: [
      buildSection('what-is', 'What is House Keeping Sweeper Service in Ahmedabad?', [
        'A house keeping sweeper in Ahmedabad handles daily sweeping, mopping, dusting, and basic cleaning of floors, corridors, and common areas. We supply trained sweepers for offices, societies, hospitals, schools, and commercial buildings so your premises stay clean and presentable.'
      ]),
      buildSection('where-used', 'Where We Provide Sweeper Staff in Ahmedabad', [], [
        'Offices and corporate buildings in Thaltej, Bodakdev, SG Highway',
        'Residential societies and apartments',
        'Hospitals, clinics, and healthcare facilities',
        'Schools, colleges, and institutes',
        'Commercial complexes and retail spaces'
      ]),
      buildSection('why-khodal', 'Why Choose ' + SITE_NAME + ' for House Keeping Sweepers?', [
        'We supply verified, trained sweepers with proper background checks. Flexible contracts—daily, monthly, or annual. One point of contact for replacement and attendance. Serving Ahmedabad and Gujarat for years.'
      ])
    ],
    faqs: [
      buildFaq('What does a house keeping sweeper do in Ahmedabad?', 'A house keeping sweeper handles sweeping, mopping, dusting, and basic cleaning of floors and common areas in offices, societies, and commercial buildings. ' + SITE_NAME + ' supplies trained sweepers across Ahmedabad and Gujarat.'),
      buildFaq('How do I hire a sweeper in Ahmedabad?', 'Contact ' + SITE_NAME + ' at +91 9265472938 or submit an inquiry on our website. We provide house keeping sweepers for offices, societies, and commercial spaces in Ahmedabad with flexible contracts.'),
      buildFaq('Do you provide sweeper staff for societies in Ahmedabad?', 'Yes. We supply society cleaning staff and house keeping sweepers for residential complexes and apartments across Ahmedabad, including Thaltej, Bodakdev, and SG Highway.'),
      buildFaq('What areas in Ahmedabad do you cover for housekeeping?', 'We serve corporate hubs such as SG Highway, Bodakdev, Thaltej, Satellite, Prahlad Nagar, and industrial belts around Naroda and Vatva, as well as societies across New West Ahmedabad. Tell us your pin code and shift timings for a tailored plan.'),
      buildFaq('How quickly can housekeeping staff start?', 'For standard office and society roles, mobilisation is typically within a few working days once commercials and ID checks are complete. For urgent event or exhibition support, call +91 9265472938 and we will advise the fastest option available.')
    ]
  },

  'office-boy-peon-ahmedabad': {
    slug: 'office-boy-peon-ahmedabad',
    name: 'Office Boy, Peon',
    metaTitle: 'Office Boy & Peon Service in Ahmedabad | Office Support Staff | ' + SITE_NAME,
    metaDescription: 'Office boy and peon service in Ahmedabad. Reliable office support staff for errands, courier, tea, and front desk. Khodal Housekeeping Agency supplies verified office boys and peons. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'office boy ahmedabad, peon service ahmedabad, office peon, office support staff ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Office Boy & Peon Service in Ahmedabad',
    intro: `${SITE_NAME} supplies office boys and peons in Ahmedabad for corporate offices, SMEs, and institutions. Handle courier, tea, errands, and front-desk support with reliable, punctual staff across Ahmedabad and Gujarat.`,
    breadcrumbName: 'Office Boy Peon Ahmedabad',
    sections: [
      buildSection('what-is', 'What does an Office Boy or Peon do in Ahmedabad?', [
        'An office boy or peon in Ahmedabad typically handles internal errands, courier dispatch and receipt, serving tea and refreshments, photocopying, and basic front-desk support. We supply verified office boys and peons so your office runs smoothly.'
      ]),
      buildSection('benefits', 'Benefits of Hiring Office Boy / Peon', [], [
        'Frees up other staff for core work',
        'Organized courier and document handling',
        'Professional impression for visitors',
        'Flexible full-time or part-time contracts'
      ]),
      buildSection('why-khodal', 'Why Choose ' + SITE_NAME + '?', [
        'We provide verified, presentable office boys and peons. Replacement and attendance managed by us. Serving corporates and SMEs across Ahmedabad, Thaltej, Bodakdev, and Gujarat.'
      ])
    ],
    faqs: [
      buildFaq('Where can I get office boy or peon in Ahmedabad?', SITE_NAME + ' supplies office boys and peons for corporate offices and SMEs in Ahmedabad. Call +91 9265472938 or use our inquiry form for a quote.'),
      buildFaq('Do you provide peon for courier and tea in Ahmedabad?', 'Yes. Our office boy and peon staff handle courier, tea, errands, and basic office support. We serve offices across Ahmedabad and Gujarat.'),
      buildFaq('Can we hire an office boy on a part-time basis?', 'Yes. We offer flexible engagement models including part-time and full-time office boys and peons in Ahmedabad, depending on your office hours and workload.'),
      buildFaq('Do you verify office support staff before deployment?', 'We follow a structured verification and orientation process so your office boy or peon understands basic etiquette, dress code, and safety norms before joining your premises.'),
      buildFaq('What if our office boy is absent?', 'You get a single point of contact at ' + SITE_NAME + ' for attendance and replacement support, so your daily office rhythm in Ahmedabad is not disrupted.')
    ]
  },

  'receptionist-ahmedabad': {
    slug: 'receptionist-ahmedabad',
    name: 'Receptionist',
    metaTitle: 'Receptionist Service in Ahmedabad | Front Desk Staff | ' + SITE_NAME,
    metaDescription: 'Receptionist and front desk staff in Ahmedabad. Professional receptionists for offices, clinics, and corporates. Khodal Housekeeping Agency supplies trained reception staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'receptionist ahmedabad, front desk staff ahmedabad, office receptionist, reception staff' + SEO_SVC_KW_TAIL,
    h1: 'Receptionist Service in Ahmedabad',
    intro: `${SITE_NAME} provides receptionist and front desk staff in Ahmedabad for offices, clinics, hotels, and corporates. Trained in greeting visitors, calls, and basic admin. Reliable reception staff across Ahmedabad and Gujarat.`,
    breadcrumbName: 'Receptionist Ahmedabad',
    sections: [
      buildSection('what-is', 'Receptionist & Front Desk Staff in Ahmedabad', [
        'A receptionist in Ahmedabad handles front desk duties: greeting visitors, answering calls, directing enquiries, and basic appointment or admin support. We supply presentable, trained receptionists for offices, clinics, and commercial spaces.'
      ]),
      buildSection('why-khodal', 'Why ' + SITE_NAME + ' for Receptionists?', [
        'Trained, presentable staff. We handle recruitment, verification, and replacement. Ideal for offices in Thaltej, Bodakdev, SG Highway, and across Ahmedabad and Gujarat.'
      ])
    ],
    faqs: [
      buildFaq('Do you supply receptionist in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies receptionist and front desk staff for offices, clinics, and corporates in Ahmedabad. Call +91 9265472938 for a quote.'),
      buildFaq('What does a receptionist do in an office?', 'Receptionists greet visitors, answer calls, direct enquiries, and support basic admin. We provide trained reception staff for offices across Ahmedabad and Gujarat.')
    ]
  },

  'helper-ahmedabad': {
    slug: 'helper-ahmedabad',
    name: 'Helper',
    metaTitle: 'Helper Service in Ahmedabad | General Helper Staff | ' + SITE_NAME,
    metaDescription: 'Helper and general support staff in Ahmedabad. Reliable helpers for loading, packing, and daily tasks. Khodal Housekeeping Agency supplies helpers for warehouses, shops, and offices. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'helper ahmedabad, helper staff, general helper, warehouse helper ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Helper Service in Ahmedabad',
    intro: `${SITE_NAME} supplies helpers in Ahmedabad for warehouses, shops, offices, and events. General support for loading, packing, moving, and day-to-day tasks. Verified helper staff across Ahmedabad and Gujarat.`,
    breadcrumbName: 'Helper Ahmedabad',
    sections: [
      buildSection('what-is', 'What is Helper Service in Ahmedabad?', [
        'Helper staff in Ahmedabad assist with loading, unloading, packing, moving, and general support in warehouses, shops, offices, and events. We supply reliable helpers so your operations run smoothly.'
      ]),
      buildSection('why-khodal', 'Why ' + SITE_NAME + ' for Helpers?', [
        'Verified helpers, flexible contracts, and replacement support. We serve Ahmedabad, Thaltej, Vastrapur, and Gujarat.'
      ])
    ],
    faqs: [
      buildFaq('Where to get helper in Ahmedabad?', SITE_NAME + ' supplies helper staff for warehouses, shops, and offices in Ahmedabad. Call +91 9265472938 for a quote.'),
      buildFaq('Do you provide helper for warehouse in Ahmedabad?', 'Yes. We provide helpers for loading, packing, and general warehouse work in Ahmedabad and across Gujarat.')
    ]
  },

  'labour-ahmedabad': {
    slug: 'labour-ahmedabad',
    publicUrlPath: '/labour-supply-ahmedabad',
    name: 'Labour',
    metaTitle:
      'Labour Supply in Ahmedabad | Manual Labour Staff | ' +
      SITE_NAME +
      ' | Labour Supply Service in Ahmedabad | Skilled & Unskilled Workers Gujarat',
    metaDescription:
      'Labour supply service in Ahmedabad. Manual labour for loading, construction support, and general work. Khodal Housekeeping Agency supplies labour on contract. Call +91 9265472938. Labour supply agency Ahmedabad: skilled and unskilled workers for warehouses, factories, construction sites & events across Naroda, Sanand, Changodar, SG Highway & Gujarat.' + SEO_SVC_DESC_TAIL,
    metaKeywords:
      'labour ahmedabad, labour supply ahmedabad, manual labour, labour contract ahmedabad, labour supply in ahmedabad, labour supply service ahmedabad, manual labour ahmedabad, contract labour supply gujarat, loading unloading labour ahmedabad, industrial labour ahmedabad, construction labour contract ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Labour Supply Service in Ahmedabad',
    intro: `${SITE_NAME} supplies labour in Ahmedabad for loading, unloading, construction support, and general manual work. Contract-based labour for industries, warehouses, and sites across Ahmedabad and Gujarat.`,
    breadcrumbName: 'Labour Ahmedabad',
    sections: [
      buildSection('what-is', 'Labour Supply in Ahmedabad', [
        'We supply manual labour in Ahmedabad for loading, unloading, construction support, and general site or warehouse work. Contract labour with clear terms and replacement support.',
        'Whether you are a logistics hub near Sanand, a retail backroom on Ashram Road, or a construction site in the outer ring, predictable labour attendance keeps your dispatch timelines honest. We align headcount with your peak windows—morning inwarding, evening dispatches, or 24x7 yard operations—so supervisors spend less time firefighting absentees.'
      ]),
      buildSection('site-types', 'Where contract labour adds the most value', [], [
        'Warehouses and fulfilment centres with palletised loads',
        'Manufacturing plants needing extra hands during PM calibrations',
        'Exhibition builds and mall refit projects with tight handover dates',
        'Construction sites requiring gang labour for shuttering or material movement',
        'Event teardown crews after large gatherings in Ahmedabad'
      ]),
      buildSection('safety', 'Safety, PPE, and site discipline', [
        'Every gang lead is briefed on helmet zones, footwear norms, and safe manual lifting. We discourage unsafe shortcuts even when clients push for speed—because one injury derails entire projects. Toolbox talks can be aligned with your EHS calendar if you share topics in advance.',
        'For night shifts, reflective vests and torch discipline are mandatory. We also document tool handovers when labour works alongside your technicians so accountability stays clear.'
      ]),
      buildSection('commercials', 'Commercial transparency', [
        'Ahmedabad’s labour market can swing during festival weeks or large industrial orders. Our quotes spell out baseline rates, overtime logic, food or transport if applicable, and replacement rules so your purchase team is not negotiating mid-month without context.',
        'Multi-site clients receive location-wise attendance exports to simplify cost allocation between Ahmedabad headquarters and satellite yards.'
      ]),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', [
        'Organized labour supply, attendance tracking, and replacement when needed. Serving industries and sites across Ahmedabad and Gujarat.',
        'Call +91 9265472938 or WhatsApp your shift pattern—we will propose gang sizes, supervision levels, and mobilisation dates tuned to your Ahmedabad location.'
      ])
    ],
    faqs: [
      buildFaq('Do you supply labour in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies labour for loading, construction support, and general manual work in Ahmedabad. Call +91 9265472938.'),
      buildFaq('What kind of labour do you provide?', 'We provide manual labour for loading, unloading, construction support, and general site or warehouse work. Contracts are flexible (daily, monthly, or project-based).'),
      buildFaq('Is labour supply available for night shifts in Ahmedabad?', 'Yes. We support day and night shifts for warehouses, logistics hubs, and industrial units in Ahmedabad where extended operations are common.'),
      buildFaq('Do you provide supervisors along with labour gangs?', 'Where required, we can structure teams with a lead hand or supervisor familiar with your site SOPs, especially for longer projects in Gujarat.'),
      buildFaq('How are wages and compliance handled?', 'Commercial terms, statutory contributions, and replacement policies are agreed upfront so your HR team has clarity while we manage day-to-day attendance with your site manager.')
    ]
  },

  'manpower-supply-ahmedabad': {
    slug: 'manpower-supply-ahmedabad',
    name: 'Manpower Supply',
    metaTitle: 'Manpower Supply in Ahmedabad | Staffing Solutions | ' + SITE_NAME,
    metaDescription: 'Manpower supply and staffing in Ahmedabad. Sweepers, peons, helpers, security, and skilled staff. Khodal Housekeeping Agency provides contract manpower across Ahmedabad & Gujarat. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'manpower supply ahmedabad, staffing ahmedabad, contract manpower, labour supply ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Manpower Supply Service in Ahmedabad',
    intro: `${SITE_NAME} is a trusted manpower supply company in Ahmedabad. We provide sweepers, peons, helpers, office staff, cleaning staff, and security personnel on contract. One stop for staffing needs across Ahmedabad and Gujarat.`,
    breadcrumbName: 'Manpower Supply Ahmedabad',
    sections: [
      buildSection('what-is', 'What is Manpower Supply in Ahmedabad?', [
        'Manpower supply in Ahmedabad means contract staffing: we provide verified staff (sweepers, peons, helpers, cleaners, security) for your premises. You get the workforce; we handle recruitment, attendance, and replacement.'
      ]),
      buildSection('types', 'Types of Manpower We Supply', [], [
        'House keeping and cleaning staff',
        'Office boy, peon, receptionist',
        'Helper, labour',
        'Security personnel',
        'Specialized cleaning (deep cleaning, carpet, pest control)'
      ]),
      buildSection('why-khodal', 'Why ' + SITE_NAME + ' for Manpower?', [
        'Single vendor for multiple staffing needs. Verified staff, proper contracts, and replacement support. Serving Ahmedabad, Thaltej, Bodakdev, SG Highway, and Gujarat.'
      ])
    ],
    faqs: [
      buildFaq('What is manpower supply in Ahmedabad?', 'Manpower supply means we provide contract staff (sweepers, peons, helpers, cleaners, etc.) for your office or site. ' + SITE_NAME + ' handles recruitment and replacement.'),
      buildFaq('Do you provide manpower for offices in Ahmedabad?', 'Yes. We supply office staff including sweepers, peons, receptionists, and helpers for offices across Ahmedabad and Gujarat. Call +91 9265472938.'),
      buildFaq('Can one vendor cover housekeeping and security together?', 'Many clients consolidate facility staffing with us so they have a single escalation path for attendance, replacements, and audits across Ahmedabad locations.'),
      buildFaq('Do you support factories outside central Ahmedabad?', 'We regularly deploy teams to industrial corridors and peripheral zones in Gujarat; share your headcount and shift pattern for a feasibility check.'),
      buildFaq('How do you price manpower contracts?', 'Pricing reflects skill mix, shift premiums, statutory costs, and contract length. A short discovery call at +91 9265472938 helps us quote accurately without hidden charges.')
    ]
  },

  'commercial-building-cleaning-staff-ahmedabad': {
    slug: 'commercial-building-cleaning-staff-ahmedabad',
    name: 'Commercial Building Cleaning Staff',
    metaTitle: 'Commercial Building Cleaning Staff in Ahmedabad | ' + SITE_NAME,
    metaDescription: 'Commercial building cleaning staff in Ahmedabad. Trained cleaners for offices, malls, and commercial complexes. Khodal Housekeeping Agency supplies cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'commercial building cleaning ahmedabad, office cleaning staff, building cleaning ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Commercial Building Cleaning Staff in Ahmedabad',
    intro: `${SITE_NAME} supplies commercial building cleaning staff in Ahmedabad for offices, malls, and commercial complexes. Sweepers, cleaners, and house keeping staff for daily upkeep. Serving Ahmedabad and Gujarat.`,
    breadcrumbName: 'Commercial Building Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Commercial Building Cleaning in Ahmedabad', ['We provide cleaning staff for commercial buildings: corridors, lobbies, washrooms, and common areas. Trained staff for offices, malls, and business parks across Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Verified staff, flexible contracts, replacement support. We serve commercial buildings in Thaltej, Bodakdev, SG Highway, and all of Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you provide cleaning staff for commercial buildings in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies sweepers and cleaning staff for commercial buildings, offices, and malls in Ahmedabad. Call +91 9265472938.')
    ]
  },

  'recency-cleaning-staff-ahmedabad': {
    slug: 'recency-cleaning-staff-ahmedabad',
    name: 'Recency Cleaning Staff',
    metaTitle: 'Recency Cleaning Staff in Ahmedabad | Regular Cleaning | ' + SITE_NAME,
    metaDescription: 'Recency cleaning staff in Ahmedabad. Regular, scheduled cleaning for offices and buildings. Khodal Housekeeping Agency supplies recency cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'recency cleaning ahmedabad, regular cleaning staff, scheduled cleaning ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Recency Cleaning Staff in Ahmedabad',
    intro: `${SITE_NAME} provides recency cleaning staff in Ahmedabad for regular, scheduled cleaning of offices and buildings. Daily or periodic cleaning to maintain hygiene and appearance. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Recency Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'What is Recency Cleaning?', ['Recency cleaning means regular, scheduled cleaning—daily or periodic—to keep premises fresh. We supply staff for sweeping, mopping, dusting, and washroom cleaning in Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Trained staff, clear schedules, and replacement when needed. Serving offices and buildings across Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('What is recency cleaning staff in Ahmedabad?', 'Recency cleaning staff perform regular scheduled cleaning (sweeping, mopping, dusting) to keep premises clean. ' + SITE_NAME + ' supplies such staff in Ahmedabad.')
    ]
  },

  'corporate-cleaning-staff-ahmedabad': {
    slug: 'corporate-cleaning-staff-ahmedabad',
    name: 'Corporate Cleaning Staff',
    metaTitle: 'Corporate Cleaning Staff in Ahmedabad | Office Cleaning | ' + SITE_NAME,
    metaDescription: 'Corporate cleaning staff in Ahmedabad. Professional cleaners for corporate offices and business parks. Khodal Housekeeping Agency supplies corporate cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'corporate cleaning ahmedabad, office cleaning staff ahmedabad, corporate office cleaning' + SEO_SVC_KW_TAIL,
    h1: 'Corporate Cleaning Staff in Ahmedabad',
    intro: `${SITE_NAME} supplies corporate cleaning staff in Ahmedabad for offices, business parks, and corporate campuses. Professional sweepers and cleaners for a polished work environment. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Corporate Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Corporate Cleaning in Ahmedabad', ['Corporate cleaning staff maintain offices, cabins, meeting rooms, and common areas. We supply trained staff for corporate offices in Thaltej, Bodakdev, SG Highway, and across Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Presentable, trained staff. We handle recruitment and replacement. Trusted by corporates across Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you provide corporate cleaning staff in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies corporate cleaning staff for offices and business parks in Ahmedabad. Call +91 9265472938 for a quote.')
    ]
  },

  'hotel-cleaning-staff-ahmedabad': {
    slug: 'hotel-cleaning-staff-ahmedabad',
    name: 'Hotel Cleaning Staff',
    metaTitle: 'Hotel Cleaning Staff in Ahmedabad | Hotel Housekeeping | ' + SITE_NAME,
    metaDescription: 'Hotel cleaning staff in Ahmedabad. Housekeeping and cleaning staff for hotels and lodges. Khodal Housekeeping Agency supplies hotel cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'hotel cleaning staff ahmedabad, hotel housekeeping ahmedabad, hotel cleaning service' + SEO_SVC_KW_TAIL,
    h1: 'Hotel Cleaning Staff in Ahmedabad',
    intro: `${SITE_NAME} supplies hotel cleaning and housekeeping staff in Ahmedabad for hotels, lodges, and guest houses. Room cleaning, lobby, and common area staff. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Hotel Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Hotel Cleaning & Housekeeping in Ahmedabad', ['We provide cleaning and housekeeping staff for hotels: room attendants, lobby cleaners, and public area staff. Trained for hygiene and guest experience.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Trained, presentable staff. Flexible contracts and replacement support. Serving hotels in Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you supply hotel cleaning staff in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies hotel cleaning and housekeeping staff for hotels and lodges in Ahmedabad. Call +91 9265472938.')
    ]
  },

  'hospital-cleaning-staff-ahmedabad': {
    slug: 'hospital-cleaning-staff-ahmedabad',
    name: 'Hospital Cleaning Staff',
    metaTitle: 'Hospital Cleaning Staff in Ahmedabad | Healthcare Cleaning | ' + SITE_NAME,
    metaDescription: 'Hospital cleaning staff in Ahmedabad. Trained cleaners for hospitals, clinics, and healthcare. Khodal Housekeeping Agency supplies hospital cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords:
      'hospital cleaning staff ahmedabad, hospital cleaning staff in ahmedabad, healthcare cleaning services ahmedabad, hospital housekeeping ahmedabad, clinical cleaning ahmedabad, hospital janitorial staff, infection control cleaning gujarat' +
      SEO_SVC_KW_TAIL,
    h1: 'Hospital Cleaning Staff in Ahmedabad',
    intro: `${SITE_NAME} supplies hospital cleaning staff in Ahmedabad for hospitals, clinics, and healthcare facilities. Hygiene-focused cleaning for wards, OPDs, and common areas. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Hospital Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Hospital Cleaning in Ahmedabad', ['Hospital cleaning staff maintain wards, OPDs, corridors, and washrooms with focus on hygiene and infection control. We supply trained staff for healthcare facilities in Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Staff trained for healthcare environments. We handle recruitment and replacement. Serving hospitals and clinics across Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you provide hospital cleaning staff in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies hospital cleaning staff for hospitals and clinics in Ahmedabad. Call +91 9265472938.')
    ]
  },

  'restaurant-cleaning-staff-ahmedabad': {
    slug: 'restaurant-cleaning-staff-ahmedabad',
    name: 'Restaurant Cleaning Staff',
    metaTitle: 'Restaurant Cleaning Staff in Ahmedabad | Kitchen & Dining Cleaning | ' + SITE_NAME,
    metaDescription: 'Restaurant cleaning staff in Ahmedabad. Cleaners for kitchens, dining, and washrooms. Khodal Housekeeping Agency supplies restaurant cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'restaurant cleaning staff ahmedabad, restaurant cleaning service, kitchen cleaning ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Restaurant Cleaning Staff in Ahmedabad',
    intro: `${SITE_NAME} supplies restaurant cleaning staff in Ahmedabad for kitchens, dining areas, and washrooms. Trained for food-safe cleaning. Restaurants and cafes across Ahmedabad and Gujarat.`,
    breadcrumbName: 'Restaurant Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Restaurant Cleaning in Ahmedabad', ['We provide staff for restaurant cleaning: kitchen, dining, and washrooms. Trained for hygiene standards suitable for food service.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Trained staff, flexible shifts, replacement support. Serving restaurants and cafes across Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you supply restaurant cleaning staff in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies restaurant cleaning staff for kitchens and dining in Ahmedabad. Call +91 9265472938.')
    ]
  },

  'cafe-cleaning-staff-ahmedabad': {
    slug: 'cafe-cleaning-staff-ahmedabad',
    name: 'Cafe Cleaning Staff',
    metaTitle: 'Cafe Cleaning Staff in Ahmedabad | Cafe Housekeeping | ' + SITE_NAME,
    metaDescription: 'Cafe cleaning staff in Ahmedabad. Cleaners for cafes and coffee shops. Khodal Housekeeping Agency supplies cafe cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'cafe cleaning staff ahmedabad, cafe cleaning service, coffee shop cleaning' + SEO_SVC_KW_TAIL,
    h1: 'Cafe Cleaning Staff in Ahmedabad',
    intro: `${SITE_NAME} supplies cafe cleaning staff in Ahmedabad for cafes and coffee shops. Counter, seating, and washroom cleaning. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Cafe Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Cafe Cleaning in Ahmedabad', ['Cafe cleaning staff keep counters, seating, and washrooms clean and presentable. We supply staff for cafes across Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Trained, presentable staff. Flexible part-time or full-time. Serving cafes in Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you provide cafe cleaning staff in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies cafe cleaning staff in Ahmedabad. Call +91 9265472938.')
    ]
  },

  'malls-cleaning-staff-ahmedabad': {
    slug: 'malls-cleaning-staff-ahmedabad',
    name: 'Malls Cleaning Staff',
    metaTitle: 'Malls Cleaning Staff in Ahmedabad | Mall Housekeeping | ' + SITE_NAME,
    metaDescription: 'Mall cleaning staff in Ahmedabad. Housekeeping for malls and multiplexes. Khodal Housekeeping Agency supplies mall cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'mall cleaning staff ahmedabad, mall housekeeping, mall cleaning service ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Malls Cleaning Staff in Ahmedabad',
    intro: `${SITE_NAME} supplies mall cleaning staff in Ahmedabad for malls, multiplexes, and retail complexes. Corridors, washrooms, and common area cleaning. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Malls Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Mall Cleaning in Ahmedabad', ['Mall cleaning staff maintain corridors, washrooms, food courts, and common areas. We supply trained staff for malls on SG Highway, Thaltej, and across Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Experienced in high-footfall spaces. We also provide valet parking staff for malls. Serving Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you supply mall cleaning staff in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies mall cleaning staff for malls and multiplexes in Ahmedabad. Call +91 9265472938.')
    ]
  },

  'showroom-cleaning-staff-ahmedabad': {
    slug: 'showroom-cleaning-staff-ahmedabad',
    name: 'Showroom Cleaning Staff',
    metaTitle: 'Showroom Cleaning Staff in Ahmedabad | ' + SITE_NAME,
    metaDescription: 'Showroom cleaning staff in Ahmedabad. Cleaners for car showrooms, furniture, and retail showrooms. Khodal Housekeeping Agency supplies showroom cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'showroom cleaning ahmedabad, showroom cleaning staff, car showroom cleaning' + SEO_SVC_KW_TAIL,
    h1: 'Showroom Cleaning Staff in Ahmedabad',
    intro: `${SITE_NAME} supplies showroom cleaning staff in Ahmedabad for car showrooms, furniture showrooms, and retail showrooms. Dusting, sweeping, and presentable upkeep. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Showroom Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Showroom Cleaning in Ahmedabad', ['Showroom cleaning staff keep showrooms dust-free and presentable. We supply staff for car, furniture, and retail showrooms across Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Trained staff, flexible contracts. Serving showrooms across Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you provide showroom cleaning staff in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies showroom cleaning staff in Ahmedabad. Call +91 9265472938.')
    ]
  },

  'shop-cleaning-staff-ahmedabad': {
    slug: 'shop-cleaning-staff-ahmedabad',
    name: 'Shop Cleaning Staff',
    metaTitle: 'Shop Cleaning Staff in Ahmedabad | Retail Cleaning | ' + SITE_NAME,
    metaDescription: 'Shop cleaning staff in Ahmedabad. Cleaners for retail shops and stores. Khodal Housekeeping Agency supplies shop cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'shop cleaning ahmedabad, retail cleaning staff, shop cleaning service' + SEO_SVC_KW_TAIL,
    h1: 'Shop Cleaning Staff in Ahmedabad',
    intro: `${SITE_NAME} supplies shop cleaning staff in Ahmedabad for retail shops and stores. Daily sweeping, dusting, and washroom cleaning. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Shop Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Shop Cleaning in Ahmedabad', ['Shop cleaning staff maintain retail shops: floors, display areas, and washrooms. We supply staff for shops across Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Reliable staff, flexible timing. Serving retail shops across Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you supply shop cleaning staff in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies shop cleaning staff for retail shops in Ahmedabad. Call +91 9265472938.')
    ]
  },

  'office-cleaning-staff-ahmedabad': {
    slug: 'office-cleaning-staff-ahmedabad',
    name: 'Office Cleaning Staff',
    metaTitle: 'Office Cleaning Staff in Ahmedabad | Office Housekeeping | ' + SITE_NAME,
    metaDescription: 'Office cleaning staff in Ahmedabad. Sweepers and cleaners for offices. Khodal Housekeeping Agency supplies office cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'office cleaning staff ahmedabad, office cleaning service, office housekeeping ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Office Cleaning Staff in Ahmedabad',
    intro: `${SITE_NAME} supplies office cleaning staff in Ahmedabad for offices of all sizes. Sweeping, mopping, dusting, and washroom cleaning. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Office Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Office Cleaning in Ahmedabad', ['Office cleaning staff handle daily sweeping, mopping, dusting, and washroom cleaning. We supply staff for offices in Thaltej, Bodakdev, SG Highway, and across Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Verified staff, flexible daily or monthly contracts. Replacement and attendance managed by us. Serving Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you provide office cleaning staff in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies office cleaning staff for offices across Ahmedabad. Call +91 9265472938.')
    ]
  },

  'pest-control-services-ahmedabad': {
    slug: 'pest-control-services-ahmedabad',
    name: 'Pest Control Services',
    metaTitle: 'Pest Control Services in Ahmedabad | Pest Control Company | ' + SITE_NAME,
    metaDescription: 'Pest control services in Ahmedabad. Rodent, cockroach, and mosquito control for offices, homes, and restaurants. Khodal Housekeeping Agency pest control. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'pest control ahmedabad, pest control services ahmedabad, rodent control, cockroach control ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Pest Control Services in Ahmedabad',
    intro: `${SITE_NAME} offers pest control services in Ahmedabad for offices, homes, restaurants, and societies. Rodent, cockroach, mosquito, and general pest control. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Pest Control Ahmedabad',
    sections: [
      buildSection('what-is', 'Pest Control in Ahmedabad', ['We provide pest control for rodents, cockroaches, mosquitoes, and other pests. Services for offices, homes, restaurants, and societies across Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Trained team, safe methods, and follow-up visits. Part of our facility management services in Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you provide pest control in Ahmedabad?', 'Yes. ' + SITE_NAME + ' offers pest control services for offices, homes, and restaurants in Ahmedabad. Call +91 9265472938.'),
      buildFaq('What pests do you control?', 'We handle rodents, cockroaches, mosquitoes, and general pest control in Ahmedabad and Gujarat.')
    ]
  },

  'society-cleaning-staff-ahmedabad': {
    slug: 'society-cleaning-staff-ahmedabad',
    name: 'Society Cleaning Staff',
    metaTitle: 'Society Cleaning Staff in Ahmedabad | Apartment Society Cleaning | ' + SITE_NAME,
    metaDescription: 'Society cleaning staff in Ahmedabad. Sweepers and cleaners for housing societies and apartments. Khodal Housekeeping Agency supplies society cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'society cleaning ahmedabad, apartment cleaning staff, society sweeper ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Society Cleaning Staff in Ahmedabad',
    intro: `${SITE_NAME} supplies society cleaning staff in Ahmedabad for housing societies, apartments, and residential complexes. Common area and lobby cleaning. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Society Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Society Cleaning in Ahmedabad', ['Society cleaning staff maintain lobbies, corridors, lifts, and common areas. We supply sweepers and cleaners for societies across Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Verified staff, society-friendly contracts. Serving societies in Thaltej, Bodakdev, Satellite, and across Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you supply society cleaning staff in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies society cleaning staff for housing societies and apartments in Ahmedabad. Call +91 9265472938.')
    ]
  },

  'valet-parking-ahmedabad': buildExpandedValetParking(buildSection, buildFaq, SITE_NAME),

  'driver-service-agent-ahmedabad': {
    slug: 'driver-service-agent-ahmedabad',
    name: 'Driver Service Agent',
    metaTitle: 'Driver Service Agent in Ahmedabad | Driver Staff | ' + SITE_NAME,
    metaDescription: 'Driver service and driver staff in Ahmedabad. Reliable drivers for corporates and offices. Khodal Housekeeping Agency supplies driver agents. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'driver service ahmedabad, driver staff ahmedabad, driver agent, office driver ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Driver Service Agent in Ahmedabad',
    intro: `${SITE_NAME} supplies driver service agents in Ahmedabad for corporates, offices, and families. Reliable drivers for daily commutes and official trips. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Driver Service Ahmedabad',
    sections: [
      buildSection('what-is', 'Driver Service in Ahmedabad', ['We provide driver staff for office vehicles, family use, and official trips. Verified drivers with proper documentation.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Verified drivers, contract-based supply. We serve corporates and families across Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you supply drivers in Ahmedabad?', 'Yes. ' + SITE_NAME + ' supplies driver service agents for corporates and offices in Ahmedabad. Call +91 9265472938.'),
      buildFaq('What is driver service agent?', 'A driver service agent is staff supplied on contract to drive office or personal vehicles. We provide verified drivers in Ahmedabad and Gujarat.')
    ]
  },

  'deep-cleaning-service-ahmedabad': {
    slug: 'deep-cleaning-service-ahmedabad',
    name: 'Deep Cleaning Service',
    metaTitle: 'Deep Cleaning Service in Ahmedabad | Professional Deep Clean | ' + SITE_NAME,
    metaDescription: 'Deep cleaning service in Ahmedabad. Thorough cleaning for offices, homes, and commercial spaces. Khodal Housekeeping Agency deep cleaning. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'deep cleaning ahmedabad, deep cleaning service ahmedabad, professional deep clean, office deep cleaning' + SEO_SVC_KW_TAIL,
    h1: 'Deep Cleaning Service in Ahmedabad',
    intro: `${SITE_NAME} offers deep cleaning service in Ahmedabad for offices, homes, and commercial spaces. Thorough cleaning beyond daily upkeep—floors, walls, corners, and hard-to-reach areas. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Deep Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'What is Deep Cleaning in Ahmedabad?', ['Deep cleaning is thorough cleaning: corners, behind furniture, grout, and areas not covered in daily cleaning. We provide deep cleaning for offices, homes, and commercial premises.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Trained team, proper equipment and products. One-time or periodic deep cleaning across Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you provide deep cleaning in Ahmedabad?', 'Yes. ' + SITE_NAME + ' offers deep cleaning service for offices, homes, and commercial spaces in Ahmedabad. Call +91 9265472938.'),
      buildFaq('What is included in deep cleaning?', 'Deep cleaning includes thorough cleaning of floors, walls, corners, behind furniture, and hard-to-reach areas. We can tailor scope to your needs.')
    ]
  },

  'special-event-cleaning-works-ahmedabad': {
    slug: 'special-event-cleaning-works-ahmedabad',
    name: 'Special Event Cleaning Works',
    metaTitle: 'Special Event Cleaning in Ahmedabad | Event Cleanup | ' + SITE_NAME,
    metaDescription: 'Special event cleaning in Ahmedabad. Pre and post-event cleanup for weddings, conferences, and exhibitions. Khodal Housekeeping Agency event cleaning. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'event cleaning ahmedabad, wedding cleanup, special event cleaning, post event cleaning ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Special Event Cleaning Works in Ahmedabad',
    intro: `${SITE_NAME} provides special event cleaning in Ahmedabad for weddings, conferences, exhibitions, and corporate events. Pre-event setup cleaning and post-event cleanup. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Special Event Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Special Event Cleaning in Ahmedabad', ['We handle pre-event cleaning (venue setup) and post-event cleanup. Weddings, conferences, exhibitions, and corporate events across Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Experienced in event timelines. We also provide valet parking and house keeping for events. One vendor for multiple event needs.'])
    ],
    faqs: [
      buildFaq('Do you do event cleaning in Ahmedabad?', 'Yes. ' + SITE_NAME + ' provides special event cleaning for weddings, conferences, and events in Ahmedabad. Call +91 9265472938.'),
      buildFaq('What is special event cleaning?', 'Special event cleaning includes pre-event venue cleaning and post-event cleanup. We handle weddings, conferences, and exhibitions in Ahmedabad and Gujarat.')
    ]
  },

  'carpet-cleaning-service-ahmedabad': {
    slug: 'carpet-cleaning-service-ahmedabad',
    name: 'Carpet Cleaning Service',
    metaTitle: 'Carpet Cleaning Service in Ahmedabad | Professional Carpet Clean | ' + SITE_NAME,
    metaDescription: 'Carpet cleaning service in Ahmedabad. Professional carpet cleaning for offices, hotels, and homes. Khodal Housekeeping Agency carpet cleaning. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'carpet cleaning ahmedabad, carpet cleaning service ahmedabad, office carpet cleaning, carpet clean ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'Carpet Cleaning Service in Ahmedabad',
    intro: `${SITE_NAME} offers carpet cleaning service in Ahmedabad for offices, hotels, and homes. Professional cleaning to remove dust, stains, and odours. Ahmedabad and Gujarat.`,
    breadcrumbName: 'Carpet Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'Carpet Cleaning in Ahmedabad', ['We provide professional carpet cleaning for offices, hotels, and residences. Cleaning methods suitable for different carpet types.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Trained team, proper equipment. One-time or periodic carpet cleaning across Ahmedabad and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you provide carpet cleaning in Ahmedabad?', 'Yes. ' + SITE_NAME + ' offers carpet cleaning service for offices, hotels, and homes in Ahmedabad. Call +91 9265472938.'),
      buildFaq('What does carpet cleaning include?', 'Carpet cleaning includes vacuuming, stain removal, and deep cleaning to remove dust and odours. We tailor the service to your carpet type and area.')
    ]
  },

  'house-cleaning-ahmedabad': {
    slug: 'house-cleaning-ahmedabad',
    name: 'House Cleaning',
    metaTitle: 'House Cleaning Service in Ahmedabad | Home Cleaning | ' + SITE_NAME,
    metaDescription: 'House cleaning service in Ahmedabad. Home cleaning, daily or periodic. Khodal Housekeeping Agency supplies house cleaning staff. Call +91 9265472938.' + SEO_SVC_DESC_TAIL,
    metaKeywords: 'house cleaning ahmedabad, home cleaning ahmedabad, house cleaning service, domestic cleaning ahmedabad' + SEO_SVC_KW_TAIL,
    h1: 'House Cleaning Service in Ahmedabad',
    intro: `${SITE_NAME} provides house cleaning service in Ahmedabad for homes and apartments. Daily, weekly, or periodic cleaning—sweeping, mopping, dusting, and kitchen and bathroom cleaning. Ahmedabad and Gujarat.`,
    breadcrumbName: 'House Cleaning Ahmedabad',
    sections: [
      buildSection('what-is', 'House Cleaning in Ahmedabad', ['House cleaning includes sweeping, mopping, dusting, kitchen and bathroom cleaning. We supply staff for daily or periodic home cleaning across Ahmedabad.']),
      buildSection('why-khodal', 'Why ' + SITE_NAME + '?', ['Verified, trusted staff. Flexible schedules. Serving homes across Ahmedabad, Thaltej, Bodakdev, and Gujarat.'])
    ],
    faqs: [
      buildFaq('Do you provide house cleaning in Ahmedabad?', 'Yes. ' + SITE_NAME + ' provides house cleaning service for homes in Ahmedabad. Call +91 9265472938 for a quote.'),
      buildFaq('What is included in house cleaning?', 'House cleaning typically includes sweeping, mopping, dusting, and kitchen and bathroom cleaning. We can tailor to daily or periodic needs.')
    ]
  }
};

export const SERVICE_LANDING_CONTENT = {
  ...SERVICE_LANDING_CONTENT_BASE,
  ...PILLAR_EXTENDED_CONTENT
};

export { PILLAR_PATH_TO_SLUG } from './seoPillarContentData';

export function getLandingContent(slug) {
  if (!slug) return null;
  return SERVICE_LANDING_CONTENT[slug] || null;
}

export function getCanonicalPathForContent(content) {
  if (!content) return '/services';
  return content.publicUrlPath || `/service/${content.slug}`;
}

/** In-page SEO cross-links only (not for site header/footer). */
export function getRelatedServiceLandingLinks(excludeSlug, maxItems = 14) {
  if (!excludeSlug) return [];
  const slugs = Object.keys(SERVICE_LANDING_CONTENT)
    .filter((slug) => slug !== excludeSlug)
    .sort();
  const seenPaths = new Set();
  const out = [];
  for (const slug of slugs) {
    if (out.length >= maxItems) break;
    const c = SERVICE_LANDING_CONTENT[slug];
    if (!c?.name) continue;
    const path = getCanonicalPathForContent(c);
    if (seenPaths.has(path)) continue;
    seenPaths.add(path);
    out.push({
      path,
      label: `${c.name} in Ahmedabad`
    });
  }
  return out;
}

export function buildLocalBusinessJsonLd(primaryServiceName, description) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: SITE_NAME,
    alternateName: [SITE_BRAND_LEGACY, SITE_BRAND_LEGACY_UPPER],
    image: SCHEMA_BUSINESS_IMAGES,
    logo: SITE_LOGO_URL,
    description: description || `${SITE_NAME} provides facility management, valet parking, housekeeping, and staffing in Ahmedabad, Gujarat.`,
    url: SITE_ORIGIN,
    telephone: '+919265472938',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Office No. 210, Ravija Plaza, Thaltej Shilaj Road',
      addressLocality: 'Ahmedabad',
      addressRegion: 'Gujarat',
      postalCode: '380059',
      addressCountry: 'IN'
    },
    geo: { '@type': 'GeoCoordinates', latitude: 23.0225, longitude: 72.5714 },
    areaServed: [{ '@type': 'City', name: 'Ahmedabad', containedInPlace: { '@type': 'State', name: 'Gujarat' } }],
    hasMap: 'https://maps.app.goo.gl/UtJBLHx2XrZozVJNA',
    makesOffer: {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: primaryServiceName,
        description: description || '',
        areaServed: 'Ahmedabad, Gujarat'
      }
    }
  };
}
